import { useTranslation } from "react-i18next";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import { useGetMachineById } from "@/machines/api/queries/machines.queries.ts";
import { generatePath, useParams } from "react-router-dom";
import PageContent from "@/shared/components/layout/dashboard-layout/page-content.tsx";
import MachinesLiveMain from "@/machines/components/common/machines-live/machines-live-main.tsx";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useGetTruckEventsOverview } from "@/machines/api/queries/events.queries.ts";
import { Clock } from "lucide-react";
import { KpiStatsCard, KpiStatsSkeleton } from "@/machines/components/common/kpi-stats";
import MachinesLiveSkeleton from "@/machines/components/common/machines-live/machines-live-skeleton.tsx";
import MachinesLiveEmpty from "@/machines/components/common/machines-live/machines-live-empty.tsx";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries.ts";
import { useGetLocationById } from "@/locations/api/queries/location.queries.ts";
import { MACHINES_URL } from "@/machines/routes.ts";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";


const MachinesLive = () => {
  const { t } = useTranslation("machines");
  const { id } = useParams();
  const machineId = id ? parseInt(id, 10) : 0;
  const { selectedOrganizationId } = useOrganizationStore();
  
  const { data: machine, status: machineStatus } = useGetMachineById(machineId);
  const { value } = useDateTimeRangeStore();
  const locationId = machine?.data?.location?.id;
  
  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(selectedOrganizationId!);
  const { data: locationData, isLoading: isLocationLoading } = useGetLocationById(locationId || 0);
  const { data: truckEventsOverview, status: truckEventsStatus } = useGetTruckEventsOverview({
    device_mxid: machine?.data?.device_mxid,
    start_time: value.fromDate,
    end_time: value.toDate,
    page: 1,
    itemsPerPage: 3000,
  });

  // Set breadcrumbs using the hook
  useSetBreadcrumbs([
    {
      path: MACHINES_URL.LOCATION,
      breadcrumb: orgData?.data?.name || t("breadcrumbs.organization"),
      type: "organizations",
      currentId: selectedOrganizationId!,
      isLoading: isOrgLoading
    },
    {
      path: generatePath(MACHINES_URL.LOCATION_DETAIL, { id: locationId?.toString() ?? "" }),
      breadcrumb: locationData?.data?.name || machine?.data?.location?.name || t("breadcrumbs.location", { id: locationId }),
      type: "locations",
      currentId: locationId,
      isLoading: isLocationLoading
    },
    {
      path: generatePath(MACHINES_URL.MACHINE_DETAIL_LIVE, { id: id ?? "" }),
      breadcrumb: machine?.data?.name || t("breadcrumbs.machine", { id }),
      type: "machines",
      currentId: machineId,
      locationId: locationId,
      isLoading: machineStatus === "pending"
    },
  ]);

  return (
    <>

      <PageContent>
        {truckEventsStatus === "pending" ? (
          <div className={"flex flex-col gap-4"}>
            <div className={"grid grid-cols-3 gap-8"}>
              <KpiStatsSkeleton title={t("live.kpis.averageCycleTime")} icon={<Clock size={16} />} />
              <KpiStatsSkeleton title={t("live.kpis.longCycles")} icon={<Clock size={16} />} />
              <KpiStatsSkeleton title={t("live.kpis.vsTarget")} icon={<Clock size={16} />} />
            </div>
            
            <MachinesLiveSkeleton />
            
            <div className={"grid grid-cols-2 gap-8"}>
              <KpiStatsSkeleton title={t("live.kpis.averageWaitTime")} icon={<Clock size={16} />} />
              <KpiStatsSkeleton title={t("live.kpis.longWaits")} icon={<Clock size={16} />} />
              <KpiStatsSkeleton title={t("live.kpis.boulderImpact")} icon={<Clock size={16} />} />
              <KpiStatsSkeleton title={t("live.kpis.boulderAnalysis")} icon={<Clock size={16} />} />
            </div>
          </div>
        ) : truckEventsOverview?.data?.graphData && truckEventsOverview?.data?.metadata ? (
          <div className={"flex flex-col gap-4"}>
            <div className={"grid grid-cols-3 gap-8"}>
              <KpiStatsCard
                title={t("live.kpis.averageCycleTime")}
                current={t("units.minutes", { value: formatNumber(truckEventsOverview.data.metadata.truckLoading.avgCycleTime, { maximumFractionDigits: 1 }) })}
                icon={<Clock size={16} />}
              />
              <KpiStatsCard
                title={t("live.kpis.longCycles")}
                current={t("live.kpis.trucksWithPercent", { value: formatNumber(truckEventsOverview.data.metadata.truckLoading.longCyclesCnt), percent: formatNumber(truckEventsOverview.data.metadata.truckLoading.longCyclesPerc, { maximumFractionDigits: 1 }) })}
                icon={<Clock size={16} />}
              />
              <KpiStatsCard
                title={t("live.kpis.vsTarget")}
                current={t("units.minutes", { value: formatNumber(truckEventsOverview.data.metadata.truckLoading.vsTarget, { maximumFractionDigits: 1 }) })}
                icon={<Clock size={16} />}
              />
            </div>
            
            <MachinesLiveMain
              metadata={truckEventsOverview.data.metadata}
              chartData={truckEventsOverview.data.graphData}
              machine={machine?.data}
            />
            
            <div className={"grid grid-cols-2 gap-8"}>
              <KpiStatsCard
                title={t("live.kpis.averageWaitTime")}
                current={t("units.minutes", { value: formatNumber(truckEventsOverview.data.metadata.waitingForTruck.avgWaitTime, { maximumFractionDigits: 1 }) })}
                icon={<Clock size={16} />}
              />
              <KpiStatsCard
                title={t("live.kpis.longWaits")}
                current={t("live.kpis.trucksWithPercent", { value: formatNumber(truckEventsOverview.data.metadata.waitingForTruck.longWaitsCnt), percent: formatNumber(truckEventsOverview.data.metadata.waitingForTruck.longWaitsCnt, { maximumFractionDigits: 1 }) })}
                icon={<Clock size={16} />}
              />
              <KpiStatsCard
                title={t("live.kpis.boulderImpact")}
                current={t("units.minutes", { value: formatNumber(truckEventsOverview.data.metadata.truckLoading.boulderImpact, { maximumFractionDigits: 1 }) })}
                icon={<Clock size={16} />}
              />
              <KpiStatsCard
                title={t("live.kpis.boulderAnalysis")}
                current={t("units.minutes", { value: truckEventsOverview.data.metadata.truckLoading.bucketAnalysis })}
                icon={<Clock size={16} />}
              />
            </div>
          </div>
        ) : (
          <MachinesLiveEmpty />
        )}
      </PageContent>
    </>
  );
};


export default MachinesLive;
